// ATLAS — the one authoritative destination hierarchy (docs/orient/ATLAS_VX_SPEC.md).
//
// Store: user_settings.oracle_spine, extended in place. Each sector node keeps
// its legacy keys (endeavour, endeavourSource, goal = deity id, aims, aimIds)
// so every existing reader keeps working, and gains an `atlas` sub-object that
// carries the rest of the S.T.A.G.E. hierarchy:
//   E  Endeavour  text = node.endeavour, deity = node.goal (legacy name), + why / arrival / vision / principles / notes
//   G  Goal       atlas.goal  — the monthly outcome (NOT the deity)
//   A  Aim        node.aims (KYRS-linked via tasks.aim_ref) + atlas.aimMeta[aimId] why / arrival
//   T  Today      atlas.today — dated; yesterday's entry reads as stale
//   S  Step       atlas.step  — dated; the current work and its completion condition
//
// TELOS (telos_settings) and onboarding (user_settings.spine.telos) data are
// imported read-only on load when an ATLAS field is empty; the old stores are
// never written here. Position is derived from evidence only (tasks), never
// typed — when there is no evidence the UI says so instead of inventing one.
import type { SupabaseClient } from '@supabase/supabase-js';
import { SECTORS, SECTOR_LABELS, type Sector } from '../domain/olmps';
import {
  effectiveAims,
  initialOracleSpine,
  buildDiffPreview,
  getOrCreateKyrsListForSector,
  delistAimTasks,
  type OracleAim,
  type OracleSpine,
  type OracleSpineNode,
} from './oracle-spine-data';
import {
  findPresetForDomain,
  resolveVisionText,
  resolveHabitNames,
  resolveActionTexts,
  resolvePrincipleTexts,
  type TelosDomainConfig,
} from './telos-cascade';
import { SECTOR_DEITY_MAP, PRESET_ENDEAVOURS } from './question-banks';
import { parseScheduled } from '../lib/autoSchedule';

export type StageKey = 'E' | 'G' | 'A' | 'T' | 'S';
/** Display order: large intentions → concrete action (spec §3A). */
export const STAGE_ORDER: StageKey[] = ['E', 'G', 'A', 'T', 'S'];
export const STAGE_NAME: Record<StageKey, string> = { E: 'Endeavour', G: 'Goal', A: 'Aim', T: 'Today', S: 'Step' };
export const STAGE_SCALE: Record<StageKey, string> = { E: '3–4 months', G: 'Month', A: 'Week', T: 'Day', S: 'Now' };
/** The two ORIENT questions at each scale (spec §8). */
export const STAGE_QUESTIONS: Record<StageKey, { where: string; arrival: string }> = {
  E: { where: 'What condition am I establishing over the next 3–4 months, and why?', arrival: 'What enduring outcome establishes the Endeavour?' },
  G: { where: 'What monthly destination advances that Endeavour?', arrival: 'What result establishes completion of this Goal?' },
  A: { where: 'What must this week accomplish?', arrival: 'What conditions establish this Aim as achieved?' },
  T: { where: 'What will be done by tonight, and why?', arrival: 'What must be true tonight for today to count?' },
  S: { where: 'Why am I doing this now?', arrival: 'What exactly constitutes finishing this Step?' },
};

export interface StageTarget {
  text: string;
  why: string;
  arrival: string;
}
export interface DatedTarget extends StageTarget {
  /** YYYY-MM-DD the entry was set for. */
  date: string;
}
export interface AtlasNodeData {
  v: 1;
  endeavour: { why: string; arrival: string; vision: string; principles: string[]; notes: string };
  goal: StageTarget;
  aimMeta: Record<string, { why: string; arrival: string }>;
  today: DatedTarget | null;
  step: DatedTarget | null;
  /** TELOS Habits — recurring behaviours, shown as support under Today. */
  habits: string[];
  /** TELOS Actions — concrete Step-level actions. */
  actions: string[];
  /** Where pre-filled values came from, until the user saves this sector. */
  imported: ('telos' | 'onboarding')[];
  /** The orb's ORIENT answer per stage + period (see orientKey): two full
   *  sentences — what I'm doing and why, and how I'll know I'm done. */
  orient?: Record<string, OrientSentences>;
  /** AI-written options for levels under a custom entry, keyed by genKey(level, parent)
   *  (atlas-tree/aiOptions.ts). */
  gen?: Record<string, string[]>;
}

export interface OrientSentences {
  doing: string;
  done: string;
}

/** Period key for an ORIENT answer: Endeavour is standing, Goal is per month,
 *  Aim per week (Monday), Today and Step per day. */
export function orientKey(stage: StageKey, day: Date): string {
  const d = new Date(day);
  if (stage === 'E') return 'E';
  if (stage === 'G') return `G:${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  if (stage === 'A') {
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return `A:${todayKey(d)}`;
  }
  return `${stage}:${todayKey(d)}`;
}

export const orientOf = (node: OracleSpineNode | null | undefined, key: string): OrientSentences | null =>
  ((node as Partial<AtlasNode> | null | undefined)?.atlas?.orient?.[key] as OrientSentences | undefined) ?? null;

/** Write one ORIENT answer into the sector's node (read-modify-write; nothing else touched). */
export async function saveOrientSentences(
  supabase: SupabaseClient,
  userId: string,
  sector: Sector,
  key: string,
  value: OrientSentences,
): Promise<void> {
  const { data, error } = await supabase.from('user_settings').select('oracle_spine').eq('user_id', userId).maybeSingle();
  if (error) throw new Error(error.message);
  const spine = ((data as { oracle_spine?: Record<string, unknown> } | null)?.oracle_spine ?? {}) as Record<string, Record<string, unknown>>;
  const node = { ...(spine[sector] ?? initialOracleSpine()[sector]) } as Record<string, unknown>;
  const atlas = { ...((node.atlas as Record<string, unknown> | undefined) ?? emptyAtlasData()) } as Record<string, unknown>;
  atlas.orient = { ...((atlas.orient as Record<string, OrientSentences> | undefined) ?? {}), [key]: value };
  node.atlas = atlas;
  const next = { ...spine, [sector]: node };
  for (const s of SECTORS) if (!next[s]) next[s] = initialOracleSpine()[s] as unknown as Record<string, unknown>;
  const { error: upErr } = await supabase
    .from('user_settings')
    .upsert({ user_id: userId, oracle_spine: next, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });
  if (upErr) throw new Error(upErr.message);
}

export type AtlasNode = OracleSpineNode & { atlas: AtlasNodeData };
export type AtlasSpine = Record<Sector, AtlasNode>;

const EMPTY_TARGET: StageTarget = { text: '', why: '', arrival: '' };

export function emptyAtlasData(): AtlasNodeData {
  return {
    v: 1,
    endeavour: { why: '', arrival: '', vision: '', principles: [], notes: '' },
    goal: { ...EMPTY_TARGET },
    aimMeta: {},
    today: null,
    step: null,
    habits: [],
    actions: [],
    imported: [],
  };
}

export const todayKey = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** A dated stage is current only on the day it was set for. */
export function isCurrent(t: DatedTarget | null | undefined, now = new Date()): boolean {
  return !!t && t.date === todayKey(now);
}

/** The dated target if it is for today, else null. */
export const currentOf = (t: DatedTarget | null | undefined, now = new Date()): DatedTarget | null =>
  t && isCurrent(t, now) ? t : null;

// ── Accessors (use these, not raw keys — `goal` is a legacy name for the deity) ──

export const deityOf = (node: OracleSpineNode | null | undefined): string | null => node?.goal ?? null;

export function deityName(sector: Sector, deityId: string | null): string | null {
  if (!deityId) return null;
  const d = SECTOR_DEITY_MAP[sector]?.find((x) => x.id === deityId);
  return d ? d.name : deityId.toUpperCase();
}

export const atlasOf = (node: OracleSpineNode | null | undefined): AtlasNodeData =>
  ((node as Partial<AtlasNode> | null | undefined)?.atlas as AtlasNodeData | undefined) ?? emptyAtlasData();

/** The real Goal stage text (monthly outcome). Empty string when undefined. */
export const goalTextOf = (node: OracleSpineNode | null | undefined): string => atlasOf(node).goal.text;

export interface StageStatus {
  defined: boolean;
  missing: ('destination' | 'why' | 'arrival')[];
}

/** Which of destination / why / arrival a stage still lacks (spec §3A, §8 "surface missing definitions"). */
export function stageStatus(node: AtlasNode, stage: StageKey, now = new Date()): StageStatus {
  const a = node.atlas;
  let t: StageTarget | null;
  switch (stage) {
    case 'E':
      t = { text: node.endeavour ?? '', why: a.endeavour.why, arrival: a.endeavour.arrival };
      break;
    case 'G':
      t = a.goal;
      break;
    case 'A': {
      const aims = effectiveAims(node);
      const first = aims[0];
      const meta = first ? a.aimMeta[first.id] : undefined;
      t = { text: first?.label ?? '', why: meta?.why ?? '', arrival: meta?.arrival ?? '' };
      break;
    }
    case 'T':
      t = currentOf(a.today, now);
      break;
    case 'S':
      t = currentOf(a.step, now);
      break;
  }
  const missing: StageStatus['missing'] = [];
  if (!t?.text.trim()) missing.push('destination');
  if (!t?.why.trim()) missing.push('why');
  if (!t?.arrival.trim()) missing.push('arrival');
  return { defined: !missing.includes('destination'), missing };
}

// ── Load (with read-only imports) ──

interface SpineSetupRow {
  visionText?: string;
  deityId?: string | null;
  aims?: { text: string; protocol: string[] }[];
}

function sectorOfTelosDomain(d: TelosDomainConfig): Sector | null {
  return SECTORS.find((s) => d.id === `telos-${SECTOR_LABELS[s].toLowerCase()}`) ?? null;
}

function normalizeNode(raw: Partial<OracleSpineNode> | undefined): AtlasNode {
  const base = initialOracleSpine().S;
  const node: OracleSpineNode = { ...base, ...(raw ?? {}) };
  if (!node.aims) {
    const back = effectiveAims(node);
    node.aims = back.length ? back : null;
  }
  const a = (raw as Partial<AtlasNode> | undefined)?.atlas;
  const data = emptyAtlasData();
  const atlas: AtlasNodeData = a
    ? {
        ...data,
        ...a,
        endeavour: { ...data.endeavour, ...(a.endeavour ?? {}) },
        goal: { ...data.goal, ...(a.goal ?? {}) },
        aimMeta: a.aimMeta ?? {},
        habits: a.habits ?? [],
        actions: a.actions ?? [],
        imported: a.imported ?? [],
        orient: a.orient ?? {},
      }
    : data;
  // isDirty = a staged proposal (SyQi stage_spine_edit) the user hasn't saved yet.
  return { ...node, isDirty: !!raw?.isDirty, atlas };
}

/** Pre-fill empty ATLAS fields from TELOS. Never overwrites a value the user set. */
function importTelos(node: AtlasNode, domain: TelosDomainConfig): AtlasNode {
  const a = { ...node.atlas, endeavour: { ...node.atlas.endeavour } };
  let touched = false;
  const vision = resolveVisionText(domain).trim();
  // Vision is the TELOS WHY. When it differs from the Endeavour it is kept as
  // supporting vision; when there is no Endeavour yet it becomes the Endeavour.
  let endeavour = node.endeavour;
  if (vision) {
    if (!endeavour) {
      endeavour = vision;
      touched = true;
    } else if (vision !== endeavour && !a.endeavour.vision) {
      a.endeavour.vision = vision;
      touched = true;
    }
  }
  // Only import cascade picks when the domain matches a preset (custom domains have none) or has custom entries.
  if (findPresetForDomain(domain) || domain.cascade?.customHabits?.length) {
    const habits = resolveHabitNames(domain);
    if (!a.habits.length && habits.length) {
      a.habits = habits;
      touched = true;
    }
    const actions = resolveActionTexts(domain);
    if (!a.actions.length && actions.length) {
      a.actions = actions;
      touched = true;
    }
    const principles = resolvePrincipleTexts(domain);
    if (!a.endeavour.principles.length && principles.length) {
      a.endeavour.principles = principles;
      touched = true;
    }
  }
  const notes = domain.cascade?.notes?.trim();
  if (notes && !a.endeavour.notes) {
    a.endeavour.notes = notes;
    touched = true;
  }
  if (!touched) return node;
  if (!a.imported.includes('telos')) a.imported = [...a.imported, 'telos'];
  return {
    ...node,
    endeavour,
    endeavourSource: node.endeavourSource ?? (endeavour ? 'manual' : null),
    atlas: a,
  };
}

/** Pre-fill from onboarding (user_settings.spine.telos) when ORACLE never saw it. */
function importOnboarding(node: AtlasNode, setup: SpineSetupRow): AtlasNode {
  let touched = false;
  let { endeavour, goal } = node;
  if (!endeavour && setup.visionText?.trim()) {
    endeavour = setup.visionText.trim();
    touched = true;
  }
  if (!goal && setup.deityId) {
    goal = setup.deityId;
    touched = true;
  }
  const a = { ...node.atlas };
  // Onboarding aims are free-text weekly intentions — keep them as Today
  // habits (supporting behaviour) rather than inventing KYRS-linked Aims.
  const aimTexts = (setup.aims ?? []).map((x) => x.text?.trim()).filter((x): x is string => !!x);
  if (aimTexts.length && !a.habits.length) {
    a.habits = aimTexts;
    touched = true;
  }
  if (!touched) return node;
  if (!a.imported.includes('onboarding')) a.imported = [...a.imported, 'onboarding'];
  return { ...node, endeavour, endeavourSource: node.endeavourSource ?? (endeavour ? 'manual' : null), goal, atlas: a };
}

/** Load the ATLAS hierarchy: oracle_spine + read-only TELOS / onboarding imports. */
export async function loadAtlas(supabase: SupabaseClient, userId: string): Promise<AtlasSpine> {
  const { data, error } = await supabase
    .from('user_settings')
    .select('oracle_spine, telos_settings, spine')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  const row = (data ?? {}) as {
    oracle_spine?: Partial<Record<Sector, Partial<OracleSpineNode>>> | null;
    telos_settings?: { domains?: TelosDomainConfig[] } | null;
    spine?: { telos?: Partial<Record<Sector, SpineSetupRow>> } | null;
  };
  const out = {} as AtlasSpine;
  for (const s of SECTORS) out[s] = normalizeNode(row.oracle_spine?.[s]);
  for (const d of row.telos_settings?.domains ?? []) {
    const s = sectorOfTelosDomain(d);
    if (s) out[s] = importTelos(out[s], d);
  }
  for (const s of SECTORS) {
    const setup = row.spine?.telos?.[s];
    if (setup) out[s] = importOnboarding(out[s], setup);
  }
  return out;
}

/**
 * Save edited sectors. Read-modify-write: only the given sectors are replaced,
 * every other sector (and any key this client doesn't know) is kept as stored,
 * so a concurrent edit from another tool or SyQi is not clobbered. Throws on error.
 */
export async function saveAtlasSectors(
  supabase: SupabaseClient,
  userId: string,
  spine: AtlasSpine,
  sectors: Sector[],
): Promise<void> {
  if (!sectors.length) return;
  const { data, error } = await supabase.from('user_settings').select('oracle_spine').eq('user_id', userId).maybeSingle();
  if (error) throw new Error(error.message);
  const current = ((data as { oracle_spine?: Record<string, unknown> } | null)?.oracle_spine ?? {}) as Record<string, unknown>;
  const next: Record<string, unknown> = { ...current };
  for (const s of sectors) {
    const prev = (current[s] ?? {}) as Record<string, unknown>;
    const node = spine[s];
    next[s] = {
      ...prev,
      ...node,
      aimIds: node.aims?.length ? node.aims.map((a) => a.id) : null,
      isDirty: false,
      atlas: { ...node.atlas, imported: [] },
    };
  }
  // Keep every sector present so legacy readers never see a missing node.
  for (const s of SECTORS) if (!next[s]) next[s] = initialOracleSpine()[s];
  const { error: upErr } = await supabase
    .from('user_settings')
    .upsert({ user_id: userId, oracle_spine: next, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });
  if (upErr) throw new Error(upErr.message);
  for (const fn of atlasSavedListeners) fn();
}

const atlasSavedListeners = new Set<() => void>();
/** Called after every successful ATLAS save (e.g. the Pantheon's SHAPE seats re-read their Endeavours). */
export function onAtlasSaved(fn: () => void): () => void {
  atlasSavedListeners.add(fn);
  return () => {
    atlasSavedListeners.delete(fn);
  };
}

/**
 * Onboarding (SpineFlow · SpineTelos) writes user_settings.spine.telos; mirror
 * that sector into the ATLAS store so there is one destination hierarchy.
 * The user just chose these, so Endeavour and deity are set; onboarding's
 * free-text weekly aims join the sector's supporting habits (deduped).
 */
export async function mirrorOnboardingSector(
  supabase: SupabaseClient,
  userId: string,
  sector: Sector,
  setup: SpineSetupRow,
): Promise<void> {
  const spine = await loadAtlas(supabase, userId);
  const node = spine[sector];
  const habits = [...node.atlas.habits];
  for (const a of setup.aims ?? []) {
    const text = a.text?.trim();
    if (text && !habits.includes(text)) habits.push(text);
  }
  spine[sector] = {
    ...node,
    endeavour: setup.visionText?.trim() || node.endeavour,
    endeavourSource: setup.visionText?.trim() ? 'manual' : node.endeavourSource,
    goal: setup.deityId ?? node.goal,
    atlas: { ...node.atlas, habits },
  };
  await saveAtlasSectors(supabase, userId, spine, [sector]);
}

// ── KYRS Aim sync (idempotent replacement for ORACLE's approve step) ──

/**
 * Bring KYRS in line with a sector's Aims: create a task for each Aim that has
 * no linked task yet (by aim_ref), and unlist tasks for Aims that were removed.
 * Safe to re-run — never inserts a duplicate.
 */
export async function syncAimTasks(
  supabase: SupabaseClient,
  userId: string,
  sector: Sector,
  before: OracleAim[],
  after: OracleAim[],
): Promise<{ added: number; removed: number }> {
  const diff = buildDiffPreview(sector, before, after);
  const wanted = after;
  const listId = await getOrCreateKyrsListForSector(supabase, userId, sector);
  let added = 0;
  if (wanted.length) {
    const refs = wanted.map((a) => `${sector}:${a.id}`);
    const { data: existing, error } = await supabase
      .from('tasks')
      .select('aim_ref, unlisted, title')
      .eq('user_id', userId)
      .in('aim_ref', refs);
    if (error) throw new Error(error.message);
    const have = new Map(((existing ?? []) as { aim_ref: string; unlisted: boolean | null; title: string | null }[]).map((r) => [r.aim_ref, r]));
    // A renamed Aim keeps its id: its task takes the new title.
    for (const a of wanted) {
      const row = have.get(`${sector}:${a.id}`);
      if (!row || row.title === a.label) continue;
      const { error: e } = await supabase
        .from('tasks')
        .update({ title: a.label, updated_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('aim_ref', `${sector}:${a.id}`);
      if (e) throw new Error(e.message);
    }
    const toRelist = refs.filter((r) => have.get(r)?.unlisted);
    if (toRelist.length) {
      const { error: e } = await supabase.from('tasks').update({ unlisted: false, updated_at: new Date().toISOString() }).eq('user_id', userId).in('aim_ref', toRelist);
      if (e) throw new Error(e.message);
    }
    const missing = wanted.filter((a) => !have.has(`${sector}:${a.id}`));
    if (missing.length) {
      const now = new Date().toISOString();
      const { error: e } = await supabase.from('tasks').insert(
        missing.map((a) => ({
          user_id: userId,
          list_id: listId,
          title: a.label,
          notes: a.cadence || null,
          done: false,
          unlisted: false,
          sector,
          aim_ref: `${sector}:${a.id}`,
          created_at: now,
          updated_at: now,
        })),
      );
      if (e) throw new Error(e.message);
      added = missing.length;
    }
  }
  // ATLAS is authoritative: any open task linked to an Aim of this sector that is no longer in
  // the hierarchy goes to the KYRS trash (restorable from the bin, same as deleting it in KYRS).
  // "Unlisted" was used before, but in KYRS that only means "not on a day" — the task stayed in
  // its list. Ticked tasks stay as history. Also covers proposals staged by SyQi.
  const keep = new Set(wanted.map((a) => `${sector}:${a.id}`));
  const { data: linked, error: lerr } = await supabase
    .from('tasks')
    .select('id, aim_ref')
    .eq('user_id', userId)
    .like('aim_ref', `${sector}:%`)
    .or('done.is.null,done.eq.false');
  if (lerr) throw new Error(lerr.message);
  const stale = ((linked ?? []) as { id: string; aim_ref: string }[]).filter((r) => !keep.has(r.aim_ref)).map((r) => r.id);
  if (stale.length) await trashTasks(supabase, userId, stale);
  // Legacy title-matched tasks (no aim_ref) for removed Aims.
  const legacy = diff.removedAims.filter((a) => !a.id.startsWith('custom-'));
  if (legacy.length) await delistAimTasks(supabase, userId, listId, legacy, sector);
  return { added, removed: stale.length };
}

/** Move tasks to the KYRS trash: snapshot each full row into `trash` (the same row KYRS's own
 *  delete writes, so the bin can restore it), then delete. Aborts before deleting if the
 *  snapshot fails, so nothing is lost. */
async function trashTasks(supabase: SupabaseClient, userId: string, ids: string[]): Promise<void> {
  const { data: rows, error } = await supabase.from('tasks').select('*').eq('user_id', userId).in('id', ids);
  if (error) throw new Error(error.message);
  const list = (rows ?? []) as ({ id: string; title?: string | null } & Record<string, unknown>)[];
  if (!list.length) return;
  const { error: te } = await supabase.from('trash').insert(
    list.map((r) => ({
      user_id: userId,
      source_component: 'Tasks',
      item_type: 'task',
      original_table: 'tasks',
      original_id: r.id,
      item_title: r.title || 'Untitled Task',
      item_data: r,
      kind: 'trash',
    })),
  );
  if (te) throw new Error(te.message);
  const { error: de } = await supabase.from('tasks').delete().eq('user_id', userId).in('id', list.map((r) => r.id));
  if (de) throw new Error(de.message);
}

// ── Evidence (position) — read-only, from tasks ──

export interface SectorEvidence {
  /** Aim-linked KYRS tasks (aim_ref) — done / total, listed only. */
  aim: { done: number; total: number; byAim: Record<string, boolean> };
  /** Tasks + METIS agenda items in this sector scheduled for today — done / total. */
  today: { done: number; total: number };
  /** Earliest open task in this sector scheduled up to the end of today. */
  nextOpen: { id: string; title: string; scheduled: string | null } | null;
  /** Alignment this week (Mon → now), from Day and Step evidence only. */
  align: AlignmentEvidence;
}
export type AtlasEvidence = Record<Sector, SectorEvidence>;

/**
 * Drift is decided by DAYS and STEPS (Joel, 2026-10-12), from real records only:
 *  - Step: every scheduled task / agenda item this week whose time has passed.
 *    Done = a hit; still open = a miss. Nothing scheduled = no evidence (never a miss).
 *  - Day: each finished weekday (Mon → yesterday, weekends off) with anything
 *    scheduled in the sector is ordered when ≥ 4 of every 5 items were done —
 *    the locked cascade rule "the goal is 4, the window is 5"
 *    (docs/OLMPS_ASTRA_CASCADE_SPEC.md). Below that it is a missed day.
 */
export interface AlignmentEvidence {
  stepHits: number;
  stepMisses: number;
  dayHits: number;
  dayMisses: number;
  /** Finished weekdays this week: null = nothing scheduled (no evidence). */
  days: { date: string; done: number; total: number; ordered: boolean | null }[];
}

/** Ratio a day must reach to count as ordered (4 of 5 — cascade spec). */
export const DAY_ORDERED_RATIO = 4 / 5;

const asSector = (v: string | null | undefined): Sector | null =>
  v && v.length === 1 && 'SHAPE'.includes(v) ? (v as Sector) : null;

/** Monday 00:00 of the week containing `now`. */
function weekStart(now: Date): Date {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  const dow = (d.getDay() + 6) % 7; // Mon = 0
  d.setDate(d.getDate() - dow);
  return d;
}

export async function loadAtlasEvidence(supabase: SupabaseClient, userId: string, now = new Date()): Promise<AtlasEvidence> {
  const out = {} as AtlasEvidence;
  for (const s of SECTORS) {
    out[s] = {
      aim: { done: 0, total: 0, byAim: {} },
      today: { done: 0, total: 0 },
      nextOpen: null,
      align: { stepHits: 0, stepMisses: 0, dayHits: 0, dayMisses: 0, days: [] },
    };
  }
  const dayStart = new Date(now);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(dayStart);
  dayEnd.setDate(dayEnd.getDate() + 1);
  const wk = weekStart(now);
  const wkKey = todayKey(wk);
  const [aimRes, taskRes, listRes, agendaRes] = await Promise.all([
    supabase.from('tasks').select('aim_ref, done, unlisted').eq('user_id', userId).not('aim_ref', 'is', null).limit(500),
    supabase.from('tasks').select('id, title, done, unlisted, scheduled, sector, list_id').eq('user_id', userId).not('scheduled', 'is', null).limit(1000),
    supabase.from('task_lists').select('id, olmps_sector').eq('user_id', userId),
    supabase
      .from('agenda_items')
      .select('id, text, date, time, completed, sector, task_id')
      .eq('user_id', userId)
      .gte('date', wkKey)
      .lte('date', todayKey(now))
      .limit(1000),
  ]);
  if (aimRes.error) throw new Error(aimRes.error.message);
  if (taskRes.error) throw new Error(taskRes.error.message);
  const listSector: Record<string, Sector> = {};
  for (const l of (listRes.data ?? []) as { id: string; olmps_sector: string | null }[]) {
    const s = asSector(l.olmps_sector);
    if (s) listSector[l.id] = s;
  }
  for (const r of (aimRes.data ?? []) as { aim_ref: string; done: boolean | null; unlisted: boolean | null }[]) {
    if (r.unlisted) continue;
    const [sec, id] = r.aim_ref.split(':');
    const s = asSector(sec);
    if (!s || !id) continue;
    out[s].aim.total += 1;
    if (r.done) out[s].aim.done += 1;
    out[s].aim.byAim[id] = !!r.done;
  }

  // One list of dated, sectored items: KYRS tasks + METIS agenda rows (agenda
  // rows linked to a task are skipped — the task already counts).
  type Item = { sector: Sector; at: Date; done: boolean; title: string | null; id: string; scheduled: string | null; task: boolean };
  const items: Item[] = [];
  for (const t of (taskRes.data ?? []) as { id: string; title: string | null; done: boolean | null; unlisted: boolean | null; scheduled: string | null; sector: string | null; list_id: string | null }[]) {
    if (t.unlisted) continue;
    const s = asSector(t.sector) ?? (t.list_id ? listSector[t.list_id] : null);
    const at = parseScheduled(t.scheduled);
    if (!s || !at) continue;
    items.push({ sector: s, at, done: !!t.done, title: t.title, id: t.id, scheduled: t.scheduled, task: true });
  }
  if (!agendaRes.error) {
    for (const a of (agendaRes.data ?? []) as { id: string; text: string | null; date: string; time: string | null; completed: boolean | null; sector: string | null; task_id: string | null }[]) {
      if (a.task_id) continue;
      const s = asSector(a.sector);
      const at = parseScheduled(a.time && /^\d{2}:\d{2}/.test(a.time) ? `${a.date} ${a.time.slice(0, 5)}` : a.date);
      if (!s || !at) continue;
      items.push({ sector: s, at, done: !!a.completed, title: a.text, id: a.id, scheduled: null, task: false });
    }
  }

  const perDay: Record<Sector, Record<string, { done: number; total: number }>> = { S: {}, H: {}, A: {}, P: {}, E: {} };
  for (const it of items.sort((x, y) => x.at.getTime() - y.at.getTime())) {
    const ev = out[it.sector];
    if (it.at >= dayStart && it.at < dayEnd) {
      ev.today.total += 1;
      if (it.done) ev.today.done += 1;
    }
    if (it.task && !it.done && it.at < dayEnd && it.title && !ev.nextOpen) {
      ev.nextOpen = { id: it.id, title: it.title, scheduled: it.scheduled };
    }
    if (it.at < wk || it.at > now) continue; // alignment window: this week, time already passed
    if (it.done) ev.align.stepHits += 1;
    else ev.align.stepMisses += 1;
    const dow = (it.at.getDay() + 6) % 7;
    if (it.at < dayStart && dow < 5) {
      const k = todayKey(it.at);
      const d = (perDay[it.sector][k] ??= { done: 0, total: 0 });
      d.total += 1;
      if (it.done) d.done += 1;
    }
  }
  // Finished weekdays (Mon → yesterday).
  for (const s of SECTORS) {
    const al = out[s].align;
    for (let d = new Date(wk); d < dayStart; d.setDate(d.getDate() + 1)) {
      if ((d.getDay() + 6) % 7 >= 5) continue;
      const k = todayKey(d);
      const c = perDay[s][k];
      const ordered = c ? c.done / c.total >= DAY_ORDERED_RATIO : null;
      al.days.push({ date: k, done: c?.done ?? 0, total: c?.total ?? 0, ordered });
      if (ordered === true) al.dayHits += 1;
      if (ordered === false) al.dayMisses += 1;
    }
  }
  return out;
}

export { PRESET_ENDEAVOURS, SECTOR_DEITY_MAP };

/** A user-written orb sentence (e.g. the DIRECT row) under atlas[map][key]; null clears it back to auto. */
export async function saveAtlasSentence(
  supabase: SupabaseClient,
  userId: string,
  sector: Sector,
  map: 'direct' | 'execute' | 'revise',
  key: string,
  value: string | null,
): Promise<void> {
  const { data, error } = await supabase.from('user_settings').select('oracle_spine').eq('user_id', userId).maybeSingle();
  if (error) throw new Error(error.message);
  const spine = ((data as { oracle_spine?: Record<string, unknown> } | null)?.oracle_spine ?? {}) as Record<string, Record<string, unknown>>;
  const node = { ...(spine[sector] ?? initialOracleSpine()[sector]) } as Record<string, unknown>;
  const atlas = { ...((node.atlas as Record<string, unknown> | undefined) ?? emptyAtlasData()) } as Record<string, unknown>;
  const m = { ...((atlas[map] as Record<string, string> | undefined) ?? {}) };
  if (value === null) delete m[key];
  else m[key] = value;
  atlas[map] = m;
  node.atlas = atlas;
  const next = { ...spine, [sector]: node };
  for (const s of SECTORS) if (!next[s]) next[s] = initialOracleSpine()[s] as unknown as Record<string, unknown>;
  const { error: upErr } = await supabase
    .from('user_settings')
    .upsert({ user_id: userId, oracle_spine: next, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });
  if (upErr) throw new Error(upErr.message);
}
export const atlasSentenceOf = (node: unknown, map: 'direct' | 'execute' | 'revise', key: string): string | null =>
  ((node as { atlas?: Record<string, Record<string, string> | undefined> } | null | undefined)?.atlas?.[map]?.[key]) ?? null;

/** "First X, then Y, and then Z." — an ordered list as one sentence. */
export function orderSentence(items: string[], more = 0): string {
  const clean = items.map((x) => x.trim().replace(/[.\s]+$/, '')).filter(Boolean);
  if (!clean.length) return '';
  const low = (x: string) => (x.length > 1 && x[1] === x[1].toLowerCase() ? x.charAt(0).toLowerCase() + x.slice(1) : x);
  const tail = more > 0 ? `, plus ${more} more` : '';
  if (clean.length === 1) return `${clean[0]}${tail}.`;
  const [first, ...rest] = clean;
  const last = rest.pop()!;
  return `First ${low(first)}${rest.map((r) => `, then ${low(r)}`).join('')}, and then ${low(last)}${tail}.`;
}
